/**
 * Design Tokens Entry Point
 *
 * Exports:
 * - primitives: Base token values (colors, spacing, typography, radius, etc.)
 * - lightTheme / darkTheme: Semantic tokens only
 * - combinedLightTheme / combinedDarkTheme: Full themes (primitives + semantic)
 * - theme: Default combined theme for backward compatibility
 * - createTheme: Helper to create combined theme from semantic tokens
 */

import { primitives } from './dist/tokens.base';
import { lightTheme } from './dist/theme.light';
import { darkTheme } from './dist/theme.dark';

// Re-export primitives
export { primitives };

// Re-export semantic themes
export { lightTheme, darkTheme };

// Types
export type { Theme } from './dist/theme.d';

/**
 * Create a combined theme object that merges primitives with semantic tokens.
 * This maintains backward compatibility with the old theme structure.
 */
export const createTheme = (semanticTheme: typeof lightTheme | typeof darkTheme) => {
  return {
    color: {
      primitive: primitives.color.primitive,
      semantic: semanticTheme.semantic,
    },
    spacing: primitives.spacing,
    typography: primitives.typography,
    radius: primitives.radius,
    border: primitives.border,
    shadow: primitives.shadow,
    opacity: primitives.opacity,
  };
};

/**
 * Combined light theme (backward compatible with old theme structure)
 */
export const combinedLightTheme = createTheme(lightTheme);

/**
 * Combined dark theme (backward compatible with old theme structure)
 */
export const combinedDarkTheme = createTheme(darkTheme);

/**
 * Default theme export (light theme) for backward compatibility
 * This matches the old `theme` export from theme.js
 */
export const theme = combinedLightTheme;

export default theme;
